import * as log from '../util/log';
import { storableError } from '../util/errors';
import {
  cancelSubscriptionApi,
  fetchSubscriptionPrice,
  initiateStripeSubscription,
  verifyCouponApi,
} from '../util/api';
import { showErrorToast, showSuccessToast } from '../util/toast';
import { StatusCode } from '../util/enums';
import { fetchCurrentUser } from './user.duck';
import { decryptNumber } from '../util/genericHelpers';

// https://stripe.com/docs/api/payment_intents/object#payment_intent_object-status
const STRIPE_PI_HAS_PASSED_CONFIRM = ['processing', 'requires_capture', 'canceled', 'succeeded'];

// ================ Action types ================ //

export const STRIPE_ACCOUNT_CLEAR_ERROR = 'app/stripe/STRIPE_ACCOUNT_CLEAR_ERROR';

export const ACCOUNT_OPENER_CREATE_REQUEST = 'app/stripe/ACCOUNT_OPENER_CREATE_REQUEST';
export const ACCOUNT_OPENER_CREATE_SUCCESS = 'app/stripe/ACCOUNT_OPENER_CREATE_SUCCESS';
export const ACCOUNT_OPENER_CREATE_ERROR = 'app/stripe/ACCOUNT_OPENER_CREATE_ERROR';

export const PERSON_CREATE_REQUEST = 'app/stripe/PERSON_CREATE_REQUEST';
export const PERSON_CREATE_SUCCESS = 'app/stripe/PERSON_CREATE_SUCCESS';
export const PERSON_CREATE_ERROR = 'app/stripe/PERSON_CREATE_ERROR';

export const CLEAR_PAYMENT_TOKEN = 'app/stripe/CLEAR_PAYMENT_TOKEN';

export const HANDLE_CARD_PAYMENT_REQUEST = 'app/stripe/HANDLE_CARD_PAYMENT_REQUEST';
export const HANDLE_CARD_PAYMENT_SUCCESS = 'app/stripe/HANDLE_CARD_PAYMENT_SUCCESS';
export const HANDLE_CARD_PAYMENT_ERROR = 'app/stripe/HANDLE_CARD_PAYMENT_ERROR';

export const HANDLE_CARD_SETUP_REQUEST = 'app/stripe/HANDLE_CARD_SETUP_REQUEST';
export const HANDLE_CARD_SETUP_SUCCESS = 'app/stripe/HANDLE_CARD_SETUP_SUCCESS';
export const HANDLE_CARD_SETUP_ERROR = 'app/stripe/HANDLE_CARD_SETUP_ERROR';

export const CLEAR_HANDLE_CARD_PAYMENT = 'app/stripe/CLEAR_HANDLE_CARD_PAYMENT';

export const RETRIEVE_PAYMENT_INTENT_REQUEST = 'app/stripe/RETRIEVE_PAYMENT_INTENT_REQUEST';
export const RETRIEVE_PAYMENT_INTENT_SUCCESS = 'app/stripe/RETRIEVE_PAYMENT_INTENT_SUCCESS';
export const RETRIEVE_PAYMENT_INTENT_ERROR = 'app/stripe/RETRIEVE_PAYMENT_INTENT_ERROR';

export const RETRIEVE_SUBSCRIPTION_PRICE_REQUEST = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_REQUEST';
export const RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS';
export const RETRIEVE_SUBSCRIPTION_PRICE_ERROR = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_ERROR';

export const VERIFY_COUPON_REQUEST = 'app/stripe/VERIFY_COUPON_REQUEST';
export const VERIFY_COUPON_SUCCESS = 'app/stripe/VERIFY_COUPON_SUCCESS';
export const VERIFY_COUPON_FAILURE = 'app/stripe/VERIFY_COUPON_FAILURE';

// Cancel subscription
export const CANCEL_SUBSCRIPTION_REQUEST = 'app/stripe/CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'app/stripe/CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_ERROR = 'app/stripe/CANCEL_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  confirmCardPaymentInProgress: false,
  confirmCardPaymentError: null,
  handleCardSetupInProgress: false,
  handleCardSetupError: null,
  paymentIntent: null,
  setupIntent: null,
  retrievePaymentIntentInProgress: false,
  retrievePaymentIntentError: null,
  subscriptionPrices: null,
  retrieveSubscriptionPriceInProgress: false,
  retrieveSubscriptionPriceError: null,
  couponLoading: false,
  couponData: null,
  couponError: null,
  cancelSubscriptionInProgress: false,
  cancelSubscriptionError: null,
  cancelSubscriptionSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STRIPE_ACCOUNT_CLEAR_ERROR:
      return { ...initialState };

    case ACCOUNT_OPENER_CREATE_REQUEST:
      return {
        ...state,
        createAccountOpenerError: null,
        createAccountOpenerInProgress: true,
      };
    case ACCOUNT_OPENER_CREATE_SUCCESS:
      return { ...state, createAccountOpenerInProgress: false, personAccountOpener: payload };
    case ACCOUNT_OPENER_CREATE_ERROR:
      console.error(payload);
      return { ...state, createAccountOpenerError: payload, createAccountOpenerInProgress: false };

    case PERSON_CREATE_REQUEST:
      return {
        ...state,
        persons: [
          ...state.persons,
          {
            ...payload,
            createStripePersonError: null,
            createStripePersonInProgress: true,
          },
        ],
      };
    case PERSON_CREATE_SUCCESS:
      return {
        ...state,
        persons: state.persons.map(p => {
          return p.personToken === payload.personToken
            ? { ...payload, createStripePersonInProgress: false }
            : p;
        }),
      };
    case PERSON_CREATE_ERROR:
      console.error(payload);
      return {
        ...state,
        persons: state.persons.map(p => {
          return p.personToken === payload.personToken
            ? { ...p, createStripePersonInProgress: false, createStripePersonError: payload.error }
            : p;
        }),
      };

    case HANDLE_CARD_PAYMENT_REQUEST:
      return {
        ...state,
        confirmCardPaymentError: null,
        confirmCardPaymentInProgress: true,
      };
    case HANDLE_CARD_PAYMENT_SUCCESS:
      return { ...state, paymentIntent: payload, confirmCardPaymentInProgress: false };
    case HANDLE_CARD_PAYMENT_ERROR:
      console.error(payload);
      return { ...state, confirmCardPaymentError: payload, confirmCardPaymentInProgress: false };

    case HANDLE_CARD_SETUP_REQUEST:
      return {
        ...state,
        handleCardSetupError: null,
        handleCardSetupInProgress: true,
      };
    case HANDLE_CARD_SETUP_SUCCESS:
      return { ...state, setupIntent: payload, handleCardSetupInProgress: false };
    case HANDLE_CARD_SETUP_ERROR:
      console.error(payload);
      return { ...state, handleCardSetupError: payload, handleCardSetupInProgress: false };

    case CLEAR_HANDLE_CARD_PAYMENT:
      return {
        ...state,
        confirmCardPaymentInProgress: false,
        confirmCardPaymentError: null,
        paymentIntent: null,
      };

    case RETRIEVE_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        retrievePaymentIntentError: null,
        retrievePaymentIntentInProgress: true,
      };
    case RETRIEVE_PAYMENT_INTENT_SUCCESS:
      return { ...state, paymentIntent: payload, retrievePaymentIntentInProgress: false };
    case RETRIEVE_PAYMENT_INTENT_ERROR:
      console.error(payload);
      return {
        ...state,
        retrievePaymentIntentError: payload,
        retrievePaymentIntentInProgress: false,
      };

    case RETRIEVE_SUBSCRIPTION_PRICE_REQUEST:
      return {
        ...state,
        retrieveSubscriptionPriceInProgress: true,
        retrieveSubscriptionPriceError: null,
      };
    case RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS:
      return {
        ...state,
        subscriptionPrices: payload,
        retrieveSubscriptionPriceInProgress: false,
        retrieveSubscriptionPriceError: null,
      };
    case RETRIEVE_SUBSCRIPTION_PRICE_ERROR:
      return {
        ...state,
        retrieveSubscriptionPriceInProgress: false,
        retrieveSubscriptionPriceError: payload,
      };
    case VERIFY_COUPON_REQUEST:
      return { ...state, couponLoading: true, couponError: null };
    case VERIFY_COUPON_SUCCESS:
      return { ...state, couponLoading: false, couponData: payload };
    case VERIFY_COUPON_FAILURE:
      return { ...state, couponLoading: false, couponError: payload };
    case CANCEL_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancelSubscriptionInProgress: true,
        cancelSubscriptionError: null,
        cancelSubscriptionSuccess: false,
      };

    case CANCEL_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        cancelSubscriptionInProgress: false,
        cancelSubscriptionError: null,
        cancelSubscriptionSuccess: true,
      };

    case CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        cancelSubscriptionInProgress: false,
        cancelSubscriptionError: action.error || 'Cancel failed',
        cancelSubscriptionSuccess: false,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const stripeAccountClearError = () => ({
  type: STRIPE_ACCOUNT_CLEAR_ERROR,
});

export const confirmCardPaymentRequest = () => ({
  type: HANDLE_CARD_PAYMENT_REQUEST,
});

export const confirmCardPaymentSuccess = payload => ({
  type: HANDLE_CARD_PAYMENT_SUCCESS,
  payload,
});

export const confirmCardPaymentError = payload => ({
  type: HANDLE_CARD_PAYMENT_ERROR,
  payload,
  error: true,
});

export const handleCardSetupRequest = () => ({
  type: HANDLE_CARD_SETUP_REQUEST,
});

export const handleCardSetupSuccess = payload => ({
  type: HANDLE_CARD_SETUP_SUCCESS,
  payload,
});

export const handleCardSetupError = payload => ({
  type: HANDLE_CARD_SETUP_ERROR,
  payload,
  error: true,
});

export const initializeCardPaymentData = () => ({
  type: CLEAR_HANDLE_CARD_PAYMENT,
});

export const retrievePaymentIntentRequest = () => ({
  type: RETRIEVE_PAYMENT_INTENT_REQUEST,
});

export const retrievePaymentIntentSuccess = payload => ({
  type: RETRIEVE_PAYMENT_INTENT_SUCCESS,
  payload,
});

export const retrievePaymentIntentError = payload => ({
  type: RETRIEVE_PAYMENT_INTENT_ERROR,
  payload,
  error: true,
});

export const retrieveSubscriptionPriceRequest = () => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_REQUEST,
});
export const retrieveSubscriptionPriceSuccess = data => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS,
  payload: data,
});
export const retrieveSubscriptionPriceError = error => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_ERROR,
  payload: error,
  error: true,
});

export const verifyCouponRequest = () => ({ type: VERIFY_COUPON_REQUEST });

export const verifyCouponSuccess = couponData => ({
  type: VERIFY_COUPON_SUCCESS,
  payload: couponData,
});

export const verifyCouponFailure = couponError => ({
  type: VERIFY_COUPON_FAILURE,
  payload: couponError,
});
export const cancelSubscriptionRequest = () => ({ type: CANCEL_SUBSCRIPTION_REQUEST });
export const cancelSubscriptionSuccess = payload => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
  payload,
});
export const cancelSubscriptionError = error => ({ type: CANCEL_SUBSCRIPTION_ERROR, error });

// ================ Selectors ================ //

export const handleCardSetupErrorSelector = state => {
  return state.stripe.handleCardSetupError;
};

export const couponDataSelectors = state => {
  return {
    couponLoading: state.stripe.couponLoading,
    couponData: state.stripe.couponData,
    couponError: state.stripe.couponError,
  };
};

// ================ Thunks ================ //

export const retrievePaymentIntent = params => dispatch => {
  const { stripe, stripePaymentIntentClientSecret } = params;
  dispatch(retrievePaymentIntentRequest());

  return stripe
    .retrievePaymentIntent(stripePaymentIntentClientSecret)
    .then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(retrievePaymentIntentSuccess(response.paymentIntent));
        return response;
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(retrievePaymentIntentError(e));

      // Log error
      const { code, doc_url, message, payment_intent } = err.error || {};
      const loggableError = err.error
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: payment_intent
              ? payment_intent.status
              : 'no payment_intent included',
          }
        : e;
      log.error(loggableError, 'stripe-retrieve-payment-intent-failed', {
        stripeMessage: loggableError.message,
      });
      throw err;
    });
};

export const confirmCardPayment = params => dispatch => {
  // It's required to use the same instance of Stripe as where the card has been created
  // so that's why Stripe needs to be passed here and we can't create a new instance.
  const { stripe, paymentParams, stripePaymentIntentClientSecret } = params;
  const transactionId = params.orderId;

  dispatch(confirmCardPaymentRequest());

  // When using default payment method paymentParams.payment_method is
  // already set Marketplace API side, when request-payment transition is made
  // so there's no need for paymentParams
  const args = paymentParams
    ? [stripePaymentIntentClientSecret, paymentParams]
    : [stripePaymentIntentClientSecret];

  const doConfirmCardPayment = () =>
    stripe.confirmCardPayment(...args).then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(confirmCardPaymentSuccess(response));
        return { ...response, transactionId };
      }
    });

  // First, check if the payment intent has already been confirmed and it just requires capture.
  return stripe
    .retrievePaymentIntent(stripePaymentIntentClientSecret)
    .then(response => {
      // Handle response.error or response.paymentIntent
      if (response.error) {
        return Promise.reject(response);
      } else if (STRIPE_PI_HAS_PASSED_CONFIRM.includes(response?.paymentIntent?.status)) {
        // Payment Intent has been confirmed already, move forward.
        dispatch(confirmCardPaymentSuccess(response));
        return { ...response, transactionId };
      } else {
        // If payment intent has not been confirmed yet, confirm it.
        return doConfirmCardPayment();
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(confirmCardPaymentError(e));

      // Log error
      const containsPaymentIntent = err.error && err.error.payment_intent;
      const { code, doc_url, message, payment_intent } = containsPaymentIntent ? err.error : {};
      const loggableError = containsPaymentIntent
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: payment_intent.status,
          }
        : e;
      log.error(loggableError, 'stripe-handle-card-payment-failed', {
        stripeMessage: loggableError.message,
      });
      throw e;
    });
};

export const retrieveSubscriptionPrice = () => async (dispatch, getState, sdk) => {
  dispatch(retrieveSubscriptionPriceRequest());

  try {
    const response = await fetchSubscriptionPrice();
    if (response && response.statusCode === StatusCode.SUCCESS) {
      dispatch(retrieveSubscriptionPriceSuccess(response.data?.productPrices || []));
    } else {
      showErrorToast('Failed to fetch subscription prices.');
    }
  } catch (e) {
    showErrorToast('Error while fetching subscription price');
    dispatch(retrieveSubscriptionPriceError(storableError(e)));
  }
};

export const initiateSubscription = params => async (dispatch, getState, sdk) => {
  try {
    const response = await initiateStripeSubscription(params);
    if (response.statusCode === StatusCode.CREATED) {
      dispatch(fetchCurrentUser());
      showSuccessToast('Successfully created subscription');
    }
    return response;
  } catch (error) {
    showErrorToast('Error while creating subscription');
    throw new Error(error.message);
  }
};

export const handleCardSetup = params => dispatch => {
  // It's required to use the same instance of Stripe as where the card has been created
  // so that's why Stripe needs to be passed here and we can't create a new instance.
  const { stripe, card, setupIntentClientSecret, paymentParams } = params;

  dispatch(handleCardSetupRequest());

  return stripe
    .handleCardSetup(setupIntentClientSecret, card, paymentParams)
    .then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(handleCardSetupSuccess(response));
        return response;
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(handleCardSetupError(e));

      // Log error
      const containsSetupIntent = err.error && err.error.setup_intent;
      const { code, doc_url, message, setup_intent } = containsSetupIntent ? err.error : {};
      const loggableError = containsSetupIntent
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: setup_intent.status,
          }
        : e;
      log.error(loggableError, 'stripe-handle-card-setup-failed', {
        stripeMessage: loggableError.message,
      });
      throw e;
    });
};

export const verifyCoupon = values => async dispatch => {
  dispatch(verifyCouponRequest());
  try {
    const response = await verifyCouponApi(values);
    response.discount = decryptNumber(response?.discount);
    dispatch(verifyCouponSuccess(response));
    return response;
  } catch (error) {
    dispatch(verifyCouponFailure(error.message));
  }
};
export const cancelSubscription = ({ subscriptionId, reason, immediate = false } = {}) => async (
  dispatch,
  getState
) => {
  dispatch(cancelSubscriptionRequest());
  try {
    // Derive subscriptionId from current user if not provided
    if (!subscriptionId) {
      const state = getState();
      const cu = state?.user?.currentUser;
      const pd = cu?.attributes?.profile?.privateData || {};
      subscriptionId = pd?.subscriptionData?.id || pd?.subscription?.id || null;
    }

    if (!subscriptionId) {
      throw new Error('No subscriptionId found for current user');
    }

    // Use cancelSubscriptionApi instead of fetch
    const body = { subscriptionId, reason, immediate };
    const json = await cancelSubscriptionApi(body);

    // Check if the response indicates an error
    if (json.error || json.message) {
      throw new Error(json.message || json.error || 'Failed to cancel subscription');
    }

    dispatch(cancelSubscriptionSuccess(json));
    // If you have a fetchCurrentUser thunk, refresh user data here:
    // await dispatch(fetchCurrentUser());
    return json;
  } catch (err) {
    dispatch(cancelSubscriptionError(err?.message || String(err)));
    return { error: err?.message || String(err) };
  }
};
